import { validateAction } from '../utils/objectActions.js';
import { getObjectPage } from '../utils/hasputils.js';
import {
    collectUsedCommandIds,
    getCommandContextError,
    hasDuplicateNavigationCommand,
    isUniqueCommand,
} from './commandContext.js';
import { getCommandDefinition } from './commandRegistry.js';
import { getActionScope, resolveActionProperty } from './actionPropertyResolver.js';
import { validatePropertyContextVariables } from './actionContextResolver.js';
import { getH3dLightProperty } from './model3dLightWireFormat.js';

/**
 * Validate one action in the context of its source object and event, as the
 * action editors show it. `handlers` are all handlers of the source object
 * (for duplicate-command rules); `handler` is the one holding `action`;
 * `nested` marks an action behind a condition or switch.
 * @returns {string} error message, or '' when valid
 */
export function validateActionInContext({ action, node, handler, handlers = [], gpioMetadata = { entities: [] }, nested = false }) {
    const scope = getActionScope(node);
    const pageId = Number(scope?.haspid);
    const sourcePage = getObjectPage(node);
    const sourcePageId = Number(sourcePage?.obj === 'template_page' ? sourcePage.targetPage : sourcePage?.haspid);
    const resolveTarget = (targetPageId, objectId) => {
        if (Number(targetPageId) !== pageId) return null;
        return (scope?.getChildren?.() ?? []).find(item => Number(item.haspid) === Number(objectId));
    };
    const resolveTemplateTarget = templateId => (node.getLayer?.()?.getChildren?.() ?? [])
        .find(item => item.obj === 'template_page' && Number(item.haspid) === Number(templateId)) ?? null;

    const base = validateAction(action, resolveTarget, resolveTemplateTarget, sourcePageId, {
        node, handler, metadata: gpioMetadata, nested,
    });
    if (base) return base;
    if (action.type === 'command') {
        const contextError = getCommandContextError(action, node, handler.event);
        if (contextError) return contextError;
        if (hasDuplicateNavigationCommand(action, handler, handlers)) {
            return 'Only one navigation command is allowed per event';
        }
        if (isUniqueCommand(getCommandDefinition(action.commandId)) &&
            collectUsedCommandIds(handlers, action.id).has(action.commandId)) {
            return 'This command is already used by another action';
        }
    }
    if (action.type === 'setObjectProperty') {
        const target = resolveTarget(action.target?.pageId, action.target?.objectId);
        return validatePropertyContextVariables(action.value, node, handler.event, resolveActionProperty(target, action.property));
    }
    if (action.type === 'model3dLight') {
        return validatePropertyContextVariables(action.value, node, handler.event, getH3dLightProperty(action.property));
    }
    return '';
}
